import { ReactNode, Ref } from 'react';

// material-ui
import { SxProps, Theme } from '@mui/material/styles';
import Card, { CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';

interface MainCardProps extends Omit<CardProps, 'title' | 'ref' | 'content' | 'sx'> {
  border?: boolean;
  boxShadow?: boolean;
  children?: ReactNode;
  subheader?: string | ReactNode;
  content?: boolean;
  contentSX?: SxProps<Theme>;
  darkTitle?: boolean;
  divider?: boolean;
  elevation?: number;
  secondary?: ReactNode;
  shadow?: string;
  sx?: SxProps<Theme>;
  title?: string | ReactNode;
  codeHighlight?: boolean;
  codeString?: string;
  modal?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export default function MainCard({
  border = true,
  boxShadow,
  children,
  subheader,
  content = true,
  contentSX = {},
  darkTitle,
  divider = true,
  elevation,
  secondary,
  shadow,
  sx = {},
  title,
  codeHighlight = false,
  codeString: _codeString,
  modal = false,
  ref,
  ...others
}: MainCardProps) {
  return (
    <Card
      elevation={elevation || 0}
      sx={[
        (theme) => ({
          position: 'relative',
          ...(border && { border: `1px solid ${(theme.vars!.palette.grey as unknown as Record<string, string>)['A800']}` }),
          borderRadius: 1,
          boxShadow: boxShadow && !border ? shadow || theme.vars!.customShadows.z1 : 'inherit',
          ':hover': { boxShadow: boxShadow ? shadow || theme.vars!.customShadows.z1 : 'inherit' },
          ...(codeHighlight && {
            '& pre': { margin: 0, padding: '12px !important', fontFamily: theme.typography.fontFamily, fontSize: '0.75rem' }
          }),
          ...(modal && {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: `calc(100% - 50px)`, sm: 'auto' },
            maxWidth: 768
          })
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
      ref={ref}
      {...others}
    >
      {/* card header and action */}
      {!darkTitle && title && (
        <CardHeader
          sx={{ p: 2.5 }}
          slotProps={{
            title: { variant: darkTitle ? 'h4' : 'subtitle1' },
            action: { sx: { m: '0px auto', alignSelf: 'center' } }
          }}
          title={title}
          action={secondary}
          subheader={subheader}
        />
      )}

      {/* content & header divider */}
      {title && divider && <Divider />}

      {/* card content */}
      {content && <CardContent sx={contentSX}>{children}</CardContent>}
      {!content && children}
    </Card>
  );
}
